// Print / Save to PDF reads the desktop <table>. On a narrow screen a DataTable shows cards instead, so
// before reading, every mounted DataTable is asked to put its desktop table on the page as well
// (off-screen, out of reach — see DataTable's PdfOffscreen) and to say when it has laid it out. A phone
// then prints the same table a big screen does. Afterwards they are told to take it down again.
//
// A plain registry rather than React context: the export is started from page handlers, outside any
// one table's tree, and a module-level set is all the coordination it needs.

const tables = new Set();

/**
 * For DataTable: `onRequest(active, done)` — `active` true asks for the desktop table, and `done()`
 * must be called once it is laid out; `active` false takes it down (no `done`). Returns the unsubscribe.
 */
export const registerPrintableTable = (onRequest) => {
  tables.add(onRequest);
  return () => tables.delete(onRequest);
};

// A table that never answers (unmounted mid-request, an error in its render) must not hang the export.
const ANSWER_TIMEOUT_MS = 1500;

const ask = (onRequest) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ANSWER_TIMEOUT_MS);
    onRequest(true, () => {
      clearTimeout(timer);
      resolve();
    });
  });

/** Runs `read` while every mounted table shows its desktop table, and returns what it returns. */
export const withDesktopTables = async (read) => {
  const current = Array.from(tables);
  if (!current.length) return read();
  await Promise.all(current.map(ask));
  try {
    return read();
  } finally {
    current.forEach((onRequest) => onRequest(false));
  }
};
